import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import patientService from '../../../../services/patient.service';
import { showError, showSuccess } from '../../../../utils/ecxSwal';

function SendPatientEmailModal({ show, onClose, clinicId, patient }) {
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            subject: '',
            content: '',
        },
    });

    const subjectValue = watch('subject');
    const contentValue = watch('content');

    const patientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
        return patient.preferredName ? `${name} (${patient.preferredName})` : name;
    }, [patient]);

    useEffect(() => {
        if (show && patient) {
            reset({ subject: '', content: '' });
            setPreviewHtml('');
            setPreviewSubject('');
            setShowPreview(false);
        }
    }, [show, patient, reset]);

    const buildPayload = (data) => ({
        subject: data.subject?.trim(),
        content: data.content?.trim(),
    });

    const handlePreview = async (data) => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setPreviewLoading(true);
        setShowPreview(true);

        try {
            const res = await patientService.previewPatientEmail(clinicId, patient.id, buildPayload(data));
            setPreviewHtml(res.data.html || '');
            setPreviewSubject(res.data.subject || data.subject);
        } catch (error) {
            console.error('Failed to preview patient email', error);
            showError({
                title: 'Preview Failed',
                text: error?.response?.data?.errorMessage || 'Could not generate the email preview.',
            });
            setShowPreview(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const onSend = async (data) => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setSending(true);

        try {
            await patientService.sendPatientEmail(clinicId, patient.id, buildPayload(data));
            showSuccess({
                title: 'Email Queued',
                text: `Email to ${patient.email} has been queued for delivery.`,
            });
            onClose();
        } catch (error) {
            console.error('Failed to send patient email', error);
            showError({
                title: 'Send Failed',
                text: error?.response?.data?.errorMessage || 'Could not queue the email for sending.',
            });
        } finally {
            setSending(false);
        }
    };

    if (!patient) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            className="patient-send-email-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-envelope me-2" />
                    Send Email
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit(onSend)}>
                <Modal.Body>
                    <div className="patient-send-email-modal__meta mb-3">
                        <div className="patient-send-email-modal__meta-item">
                            <span className="patient-send-email-modal__meta-label">Patient</span>
                            <span className="patient-send-email-modal__meta-value">{patientLabel}</span>
                        </div>
                        <div className="patient-send-email-modal__meta-item">
                            <span className="patient-send-email-modal__meta-label">To</span>
                            <span className="patient-send-email-modal__meta-value">{patient.email}</span>
                        </div>
                    </div>

                    <Form.Group className="mb-3" controlId="patientEmailSubject">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter email subject"
                            isInvalid={!!errors.subject}
                            {...register('subject', { required: 'Subject is required' })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.subject?.message}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            You can use placeholders like {'{PATIENT_NAME}'} or {'{CLINIC_NAME}'}.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="patientEmailContent">
                        <Form.Label>Content</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={8}
                            placeholder="Write the email message for this patient"
                            isInvalid={!!errors.content}
                            {...register('content', { required: 'Content is required' })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.content?.message}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            Preview wraps this content in your clinic&apos;s CLINIC_EMAIL template.
                        </Form.Text>
                    </Form.Group>

                    {showPreview && (
                        <div className="patient-send-email-modal__preview">
                            <div className="patient-send-email-modal__preview-header">
                                <h6 className="mb-1 fw-semibold">Preview</h6>
                                <span className="text-muted small">
                                    {previewSubject || subjectValue || 'Subject preview'}
                                </span>
                            </div>

                            <div className="patient-send-email-modal__preview-body">
                                {previewLoading ? (
                                    <div className="patient-send-email-modal__preview-loading">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Generating preview...
                                    </div>
                                ) : previewHtml ? (
                                    <div
                                        className="patient-send-email-modal__preview-html"
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                ) : (
                                    <p className="text-muted mb-0 small">No preview available.</p>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} disabled={sending || previewLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="outline-primary"
                        type="button"
                        disabled={sending || previewLoading || !subjectValue?.trim() || !contentValue?.trim()}
                        onClick={handleSubmit(handlePreview)}
                    >
                        {previewLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Previewing...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-eye me-1" />
                                Preview
                            </>
                        )}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={sending || previewLoading || !subjectValue?.trim() || !contentValue?.trim()}
                    >
                        {sending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-send me-1" />
                                Send Email
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default SendPatientEmailModal;
